import { useState } from 'react';
import { useSettingsStore, useAuthStore } from '@/stores';
import { Button } from '@/components/common/Button';
import { useToast } from '@/components/common/Toast';
import {
  Building2, Key, CheckCircle2, AlertTriangle, Loader2, ShieldCheck,
  Store, Hash, MapPin
} from 'lucide-react';
import { clsx } from 'clsx';

export function BusinessConfig() {
  const [freshaTokenInput, setFreshaTokenInput] = useState('');
  const [isConnectingFresha, setIsConnectingFresha] = useState(false);

  const { businessName, locationAliases, locationNameMode, setSetting } = useSettingsStore();
  const { user, token, freshaConnected, freshaProvider, workspaceId, locations, initialize } = useAuthStore();
  const { showToast } = useToast();

  const isAdmin = user?.role === 'admin';

  const handleUpdateFreshaSession = async () => {
    const rawToken = freshaTokenInput.trim();
    if (!rawToken) {
      showToast('Por favor ingresa la cookie de sesión de Fresha', 'error');
      return;
    }

    setIsConnectingFresha(true);
    try {
      const res = await fetch('/api/admin/fresha-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        body: JSON.stringify({ token: rawToken }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok || !result.success) {
        showToast(result.error || 'Error al verificar sesión de Fresha', 'error');
        return;
      }

      showToast(`¡Fresha conectado con éxito! (${result.data?.providerName || 'Partner'})`, 'success');
      setFreshaTokenInput('');
      await initialize();
    } catch (err: any) {
      showToast(err?.message || 'Error al conectar con el servidor', 'error');
    } finally {
      setIsConnectingFresha(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Perfil del Salón / Nombre Comercial */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-3 shadow-xs">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
            <Building2 size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Identidad del Negocio</h3>
            <p className="text-[11px] text-text-muted">Nombre de marca que se insertará en las etiquetas &#123;negocio&#125;</p>
          </div>
        </div>

        <div className="space-y-1.5 pt-1">
          <label className="text-xs font-medium text-text-primary block">
            Nombre Comercial / Marca
          </label>
          <input
            type="text"
            value={businessName || ''}
            onChange={(e) => setSetting('businessName', e.target.value)}
            placeholder={freshaProvider ? `Ej: ${freshaProvider} (Deducido de Fresha)` : 'Ej: Depilaway'}
            className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs font-medium text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
          />
          <p className="text-[11px] text-text-muted">
            {freshaProvider
              ? `Deducido automáticamente de tu cuenta de Fresha ("${freshaProvider}"). Puedes escribir un nombre personalizado aquí para sobrescribirlo.`
              : 'Nombre del centro que se incluirá en los mensajes donde uses la etiqueta {negocio}.'}
          </p>
        </div>
      </div>

      {/* Gestión y Nombres de Sedes */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-3 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
              <MapPin size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary">Gestión y Nombres de Sedes</h3>
              <p className="text-[11px] text-text-muted">Personaliza cómo se mostrarán los nombres de tus sucursales en citas, CRM y WhatsApp</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 bg-surface-secondary border border-border rounded-lg px-2 py-1 text-xs shrink-0 self-start sm:self-auto">
            <span className="text-[10px] text-text-muted font-medium">Modo:</span>
            <select
              value={locationNameMode}
              onChange={(e) => setSetting('locationNameMode', e.target.value as any)}
              className="bg-transparent text-xs font-semibold text-brand-primary focus:outline-none cursor-pointer"
            >
              <option value="alias" className="bg-surface text-text-primary">Alias Personalizado</option>
              <option value="internal" className="bg-surface text-text-primary">Nombre Interno Fresha</option>
              <option value="public" className="bg-surface text-text-primary">Nombre Público Fresha</option>
            </select>
          </div>
        </div>

        {locations.length === 0 ? (
          <div className="p-3 bg-surface-secondary rounded-xl text-center text-xs text-text-muted border border-border">
            No se han detectado sedes vinculadas a tu cuenta de Fresha.
          </div>
        ) : (
          <div className="space-y-2 pt-1">
            {locations.map((loc) => {
              const locId = String(loc.id);
              const publicName = loc.attributes?.name || (loc as any).name || '';
              const internalName = loc.attributes?.['internal-name'] || (loc.attributes as any)?.internalName || '';
              const currentAlias = locationAliases?.[locId] || '';

              return (
                <div key={locId} className="p-3 bg-surface-secondary border border-border rounded-xl space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-text-primary">{publicName || internalName || `Sede #${locId}`}</span>
                      <span className="font-mono text-[10px] text-text-muted bg-surface px-1.5 py-0.5 rounded border border-border">
                        ID: {locId}
                      </span>
                    </div>

                    <div className="text-[10px] text-text-muted flex items-center gap-2">
                      {publicName && <span>Público: <strong className="text-text-secondary">{publicName}</strong></span>}
                      {internalName && <span>Interno: <strong className="text-text-secondary">{internalName}</strong></span>}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-medium text-text-muted block">
                      Nombre para mostrar / Alias en PWA y WhatsApp:
                    </label>
                    <input
                      type="text"
                      value={currentAlias}
                      onChange={(e) => {
                        const val = e.target.value;
                        const next = { ...(locationAliases || {}), [locId]: val };
                        if (!val.trim()) delete next[locId];
                        setSetting('locationAliases', next);
                      }}
                      placeholder={`Ej: ${internalName || publicName || 'Nombre de la sede'}`}
                      className="w-full px-3 py-1.5 bg-surface border border-border rounded-lg text-xs font-medium text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Fresha API Integration Card */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-4 shadow-xs">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={clsx(
              'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
              freshaConnected ? 'bg-state-confirmed/10 text-state-confirmed' : 'bg-brand-danger/10 text-brand-danger'
            )}>
              <Key size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary">Vinculación con Fresha</h3>
              <p className="text-[11px] text-text-muted">
                Conexión protegida y guardada en el servidor
              </p>
            </div>
          </div>

          {/* Connection Status Pill */}
          <div className={clsx(
            'px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1.5',
            freshaConnected
              ? 'bg-state-confirmed/10 text-state-confirmed border border-state-confirmed/20'
              : 'bg-brand-danger/10 text-brand-danger border border-brand-danger/20'
          )}>
            {freshaConnected ? (
              <>
                <CheckCircle2 size={12} />
                <span>Conectado</span>
              </>
            ) : (
              <>
                <AlertTriangle size={12} />
                <span>Desconectado</span>
              </>
            )}
          </div>
        </div>

        {/* Provider Details Info Box */}
        {freshaConnected && (
          <div className="p-3 bg-surface-secondary border border-border rounded-xl grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <Store size={14} className="text-brand-primary shrink-0" />
              <div>
                <span className="text-[10px] text-text-muted block">Negocio Vinculado</span>
                <span className="font-semibold text-text-primary">{freshaProvider || 'Fresha Partner'}</span>
              </div>
            </div>

            {workspaceId && (
              <div className="flex items-center gap-2 sm:justify-end">
                <Hash size={14} className="text-text-muted shrink-0" />
                <div className="sm:text-right">
                  <span className="text-[10px] text-text-muted block">Identificador del Negocio</span>
                  <span className="font-mono text-[11px] text-text-secondary">{workspaceId}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Admin Controls */}
        {isAdmin ? (
          <div className="space-y-3 pt-2 border-t border-border-light">
            <div>
              <label className="text-xs font-semibold text-text-primary block mb-1">
                Actualizar Cookie de Sesión Fresha (Partners)
              </label>
              <p className="text-[11px] text-text-muted mb-2">
                Pega el valor de la cookie <code className="bg-surface-secondary px-1 py-0.5 rounded text-text-primary font-mono text-[10px]">fresha_partners_session</code> para mantener conectado tu salón con Fresha.
              </p>
              <textarea
                value={freshaTokenInput}
                onChange={(e) => setFreshaTokenInput(e.target.value)}
                placeholder="Pega aquí la cookie fresha_partners_session..."
                rows={2}
                className="w-full p-2.5 bg-surface-secondary border border-border rounded-xl text-xs font-mono text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              />
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                disabled={isConnectingFresha || !freshaTokenInput.trim()}
                onClick={handleUpdateFreshaSession}
                className="text-xs"
              >
                {isConnectingFresha ? (
                  <>
                    <Loader2 size={13} className="animate-spin mr-1" />
                    <span>Verificando con Fresha...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={13} className="mr-1" />
                    <span>Verificar y Conectar Fresha</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-2.5 bg-surface-secondary rounded-xl text-[11px] text-text-muted flex items-center gap-2 border border-border">
            <ShieldCheck size={14} className="text-brand-primary shrink-0" />
            <span>Solo los administradores pueden modificar la credencial maestra de Fresha.</span>
          </div>
        )}
      </div>
    </div>
  );
}
